import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  createServiceCategory,
  findServiceCategory,
  patchServiceCategory,
} from '../../store/serviceCategory/api';
import {
  reset,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../store/serviceCategory/serviceCategorySlice';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import useToggle from '../../hooks/useToggle';
import { useFormik } from 'formik';
import { omit } from 'lodash';
import serviceCategorySchema from '../../validation/serviceCategorySchema';
import ServiceCategoryFormModal from './ServiceCategoryFormModal';
import { getAssetURl } from '../../utils/url';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.serviceCategory);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [search, setSearch] = useState('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findServiceCategory({
          limit,
          skip,
          ...(search && { search }),
        })
      );
    }
  }, [dispatch, status, search, limit, skip]);

  useEffect(() => {
    dispatch(reset());
  }, [search, dispatch]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const formik = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      image: '',
      position: '1',
      activeState: true,
    },
    validationSchema: serviceCategorySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchServiceCategory(values)).unwrap();
        } else {
          await dispatch(createServiceCategory(omit(values, ['id']) as any)).unwrap();
        }
        onClose();
        formik.resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch {
        /* toast handled */
      }
    },
  });

  return (
    <Page title='Event Service Categories'>
      <Container maxWidth='xl'>
        <HeaderBreadcrumbs
          heading='Event Service Categories'
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={onOpen}
            >
              New Category
            </Button>
          }
        />
        <Card>
          <Searchbar handleSearch={(e: any) => setSearch(e.target.value)} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative', width: '100%' }}>
              <Table size='medium' sx={{ minWidth: 1000, width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Image</TableCell>
                    <TableCell align='center'>Label EN</TableCell>
                    <TableCell align='center'>Label AR</TableCell>
                    <TableCell align='center'>Position</TableCell>
                    <TableCell align='center'>Status</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell colSpan={6} align='center'>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align='center'>
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {row.image ? (
                            <Avatar
                              alt={row.labelEn}
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(row.image, assetUrl)}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align='center'>{row.labelEn}</TableCell>
                        <TableCell align='center'>{row.labelAr}</TableCell>
                        <TableCell align='center'>{row.position}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={row.activeState ? 'success' : 'error'}
                          >
                            {row.activeState ? 'Active' : 'Inactive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            onClick={() => {
                              formik.setValues({
                                id: row.id,
                                labelEn: row.labelEn,
                                labelAr: row.labelAr,
                                image: row.image || '',
                                position: String(row.position || 1),
                                activeState: row.activeState,
                              });
                              onOpen();
                            }}
                          >
                            <Iconify icon='eva:edit-fill' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(
                Math.max(Math.ceil(totalRecords / limit) - 1, 0),
                currentPage
              )}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
        <ServiceCategoryFormModal
          toggle={toggle}
          onClose={() => {
            formik.resetForm();
            onClose();
          }}
          formik={formik}
        />
      </Container>
    </Page>
  );
};

export default Index;
